// GPU texture memory estimates (bytes). Approximate: what the driver allocates for the texture and its mips.

import {
  RGBA_ASTC_4x4_Format,
  RGBA_BPTC_Format,
  RGBA_ETC2_EAC_Format,
  RGBA_S3TC_DXT5_Format,
  RGB_ETC1_Format,
  RGB_ETC2_Format,
  RGB_PVRTC_4BPPV1_Format,
  RGBA_PVRTC_4BPPV1_Format,
  RGB_S3TC_DXT1_Format,
  RGBA_S3TC_DXT1_Format,
  HalfFloatType,
  FloatType,
  LinearFilter,
  NearestFilter,
  type CompressedTexture,
  type Texture,
} from 'three';

const BITS: Record<number, number> = {
  [RGB_S3TC_DXT1_Format]: 4,
  [RGBA_S3TC_DXT1_Format]: 4,
  [RGBA_S3TC_DXT5_Format]: 8,
  [RGB_ETC1_Format]: 4,
  [RGB_ETC2_Format]: 4,
  [RGBA_ETC2_EAC_Format]: 8,
  [RGBA_ASTC_4x4_Format]: 8,
  [RGBA_BPTC_Format]: 8,
  [RGB_PVRTC_4BPPV1_Format]: 4,
  [RGBA_PVRTC_4BPPV1_Format]: 4,
};

export function estimateTextureBytes(t: Texture): number {
  const ct = t as CompressedTexture;
  if (ct.isCompressedTexture && Array.isArray(ct.mipmaps) && ct.mipmaps.length) {
    const bits = BITS[ct.format as number];
    const w = ct.mipmaps[0]!.width, h = ct.mipmaps[0]!.height;
    if (bits) return Math.round(((w * h * bits) / 8) * (ct.mipmaps.length > 1 ? 4 / 3 : 1));
    let sum = 0;
    for (const m of ct.mipmaps) sum += (m as { data: ArrayBufferView }).data?.byteLength ?? 0;
    return sum;
  }
  const img = t.image as { width?: number; height?: number; videoWidth?: number; videoHeight?: number } | null;
  const w = img?.videoWidth || img?.width || 0;
  const h = img?.videoHeight || img?.height || 0;
  const bpp = t.type === FloatType ? 16 : t.type === HalfFloatType ? 8 : 4;
  const mips = t.generateMipmaps && t.minFilter !== LinearFilter && t.minFilter !== NearestFilter ? 4 / 3 : 1;
  return Math.round(w * h * bpp * mips);
}

export function formatBytes(n: number): string {
  if (n >= 1 << 20) return `${(n / (1 << 20)).toFixed(1)} MB`;
  if (n >= 1 << 10) return `${(n / (1 << 10)).toFixed(0)} KB`;
  return `${n} B`;
}
